import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas.ts'
import {UIMSceneEnum} from '../../../../shared/enums/UIMSceneEnums.ts'
import {MobileWebviewCommunicationActions} from '@colonist/mobile'
import {notificationController, socket, uiGameManager} from '../../../shared/scripts/EntryShared.ts'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables.ts'
import {CA} from '../../../shared/scripts/analytics/Analytics.ts'
import {UIMMobileWebviewCommunication} from '../ui_mobile_communication/UIMMobileWebviewCommunication.ts'
import {type UIMManager} from './UIMManager.ts'
import {UIOnlineFriendController} from '../../../shared/scripts/ui_friends/UIOnlineFriendController.ts'
import {UIMDeviceHandler} from './UIMDeviceHandler.ts'

export class UIMSceneController {
    private readonly uiManager: UIMManager
    private _scene: UIMSceneEnum | undefined = UIMSceneEnum.Homepage

    constructor(uiManager: UIMManager) {
        this.uiManager = uiManager
    }

    get scene(): UIMSceneEnum | undefined {
        return this._scene
    }

    loadScene(scene: UIMSceneEnum | undefined) {
        if(scene == undefined) {
            this._scene = scene
            this.uiManager.mainpageController.navigationController.deselectAllTabs()
            return
        }

        if(this._scene == scene) return
        this._scene = scene

        this.closeOverlays()

        if(scene == UIMSceneEnum.Game) return void this.loadGame()
        const isPlayerInCreateRoomFlow = this.uiManager.createRoomController.isUserOnCreateRoomFlow()
        if(isPlayerInCreateRoomFlow) return this.uiManager.createRoomController.onExitRoom(() => this.goToTab(scene), true)
    }

    closeOverlays() {
        this.uiManager.mainpageController.profileOverlayController.hideAndClearPreviousScene()
        this.uiManager.mainpageController.bodyController.modeSelectorDetailsController.hideAndClearPreviousScene()
        this.goToTab(this._scene ?? UIMSceneEnum.Homepage)
    }

    private goToTab(scene: UIMSceneEnum) {
        const shouldMakeProfileFourthSection = UIUtilsEjsVariables.shouldHideStoreOnMobile && scene == UIMSceneEnum.More
        const updatedScene = shouldMakeProfileFourthSection ? scene - 1 : scene

        this.uiManager.mainpageController.bodyController.swipeNavigation.go(updatedScene)
        this.uiManager.mainpageController.navigationController.moveSliderTo(scene)

        if(scene == UIMSceneEnum.Lobby) this.uiManager.mainpageController.bodyController.lobbyController.show()
        if(scene == UIMSceneEnum.Homepage) this.uiManager.mainpageController.bodyController.onHomepageShow()
        if(scene == UIMSceneEnum.Store) this.uiManager.mainpageController.bodyController.onStoreShow()
    }

    async loadGame() {
        this.uiManager.createRoomController.disableChatInput()
        const didPauseForKeyboard = await UIMDeviceHandler.pauseForKeyboardToClose()

        this.keepConnectionAlive()
        UIOnlineFriendController.userEnteredGame()
        notificationController.removeDisconnectBanners()
        this.uiManager.createRoomController.leaveRoom()
        this.uiManager.adController.hideAllAds()
        this.uiManager.mainpageController.bodyController.lobbyController.hide()
        this.uiManager.mainpageController.bodyController.hide()
        this.uiManager.mainpageController.headerController.hide()
        this.uiManager.mainpageController.navigationController.hide()
        this.uiManager.singletonPageController.closeAllPages()
        this.uiManager.singletonPageController.removeLinkListener()

        uiGameManager.canvasController.buildGame()
        if(didPauseForKeyboard) uiGameManager.canvasController.responsiveController.resizeScreen()

        UIMMobileWebviewCommunication.sendPayloadToMobile(MobileWebviewCommunicationActions.GameStarted)

        const abTestEventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.MobileGameStarted,
            category: AnalyticEventCategory.MobileAnalytics,
            floatValue: Number(socket.gameListener.databaseGameId),
        }
        void CA.sendAnalyticEventsWithMobileDimensions(abTestEventData)

        if(UIUtilsEjsVariables.shouldDisplayNewMobileGameUI) {
            window.addEventListener('pagehide', () => UIMMobileWebviewCommunication.sendPayloadToMobile(MobileWebviewCommunicationActions.ShowSplashScreen, true))
            return
        }

        const sendRotation = setInterval(() => {
            const canvas = document.getElementsByTagName('canvas')
            if(canvas.length == 0) return
            clearInterval(sendRotation)
            UIMMobileWebviewCommunication.sendPayloadToMobile(MobileWebviewCommunicationActions.OrientationLandscape)
        }, 500)
    }

    // eslint-disable-next-line class-methods-use-this
    private keepConnectionAlive() {
        setInterval(() => {
            socket?.controller.sender.sendNoop()
        }, 10000)
    }
}
